import { useReducer, useCallback, useEffect } from 'react';
import type { WidgetState, WidgetAction, FilterState, BudgetData, FilterOptions, SelectedTitle, DisplayUnit } from '../types';

const initialState: WidgetState = {
  filters: {
    einzelplan: null,
    kapitel: null,
    query: null,
    expenditureType: null,
    subExpType: null,
    funktion: null,
    oberfunktion: null,
    hauptfunktion: null,
    gruppierung: null,
    obergruppe: null,
    hauptgruppe: null,
    prefix: null,
    kapitelScope: null,
    type: 'A',
  },
  // null = not chosen yet; BudgetWidget resolves it to the newest year the
  // data actually has, so the widget follows each new Haushalt on its own.
  selectedYear: null,
  drillDownType: null,
  drillDownSubType: null,
  isLoading: true,
  error: null,
  data: null,
  filterOptions: null,
  selectedTitle: null,
  displayUnit: 'absolute',
  brokenOutTitleIds: [],
};

// A plain sub_expenditure_type filter (no "parent|sub" separator) comes from a
// cross-parent spotlight — e.g. "Bildung", which exists under two expenditure
// types (Öffentliche Leistungen, Subventionen an Haushalte). Unlike a compound
// subExpType produced by drilling, this one must SURVIVE drill navigation:
// otherwise drilling into one of its parents would drop the Bildung constraint
// and show the parent's entire spending. Returns the value to preserve, or null.
export function crossParentSubExpType(filters: FilterState): string | null {
  const s = filters.subExpType;
  return s && !s.includes('|') ? s : null;
}

export function widgetReducer(state: WidgetState, action: WidgetAction): WidgetState {
  switch (action.type) {
    case 'SET_FILTER': {
      // Only reset drill-down for navigation filters, not business filters
      const isNavigationFilter = action.key === 'expenditureType' || action.key === 'subExpType';
      const isBusinessFilter = action.key === 'einzelplan' || action.key === 'kapitel';

      // Cascading clear logic for Funktion hierarchy
      // When hauptfunktion changes, clear oberfunktion and funktion
      // When oberfunktion changes, clear funktion
      let funktionCascade = {};
      if (action.key === 'hauptfunktion' && action.value !== state.filters.hauptfunktion) {
        funktionCascade = { oberfunktion: null, funktion: null };
      } else if (action.key === 'oberfunktion' && action.value !== state.filters.oberfunktion) {
        funktionCascade = { funktion: null };
      }

      // Cascading clear logic for Gruppierung hierarchy
      // When hauptgruppe changes, clear obergruppe and gruppierung
      // When obergruppe changes, clear gruppierung
      let gruppierungCascade = {};
      if (action.key === 'hauptgruppe' && action.value !== state.filters.hauptgruppe) {
        gruppierungCascade = { obergruppe: null, gruppierung: null };
      } else if (action.key === 'obergruppe' && action.value !== state.filters.obergruppe) {
        gruppierungCascade = { gruppierung: null };
      }

      return {
        ...state,
        filters: {
          ...state.filters,
          [action.key]: action.value,
          // Clear kapitel when einzelplan changes
          ...(action.key === 'einzelplan' && action.value !== state.filters.einzelplan
            ? { kapitel: null }
            : {}),
          ...funktionCascade,
          ...gruppierungCascade,
        },
        // Reset drill-down when navigation filters change
        ...(isNavigationFilter ? {
          drillDownType: null,
          drillDownSubType: null,
          selectedTitle: null,
        } : {}),
        // Clear selected title when business filters change (but keep drill-down)
        ...(isBusinessFilter ? { selectedTitle: null } : {}),
        // Any filter change re-scopes which titles exist, so a break-out from
        // Sonstiges no longer applies.
        brokenOutTitleIds: [],
      };
    }

    case 'SET_FILTER_WITH_DRILL_DOWN':
      return {
        ...state,
        filters: {
          ...state.filters,
          [action.key]: action.value,
        },
        drillDownType: action.drillDownType,
        drillDownSubType: action.drillDownSubType,
        selectedTitle: null,
        brokenOutTitleIds: [],
      };

    case 'SET_FILTERS_WITH_DRILL_DOWN':
      return {
        ...state,
        filters: {
          ...state.filters,
          ...action.filters,
        },
        drillDownType: action.drillDownType,
        drillDownSubType: action.drillDownSubType,
        selectedTitle: null,
        brokenOutTitleIds: [],
      };

    case 'CLEAR_FILTERS':
      return {
        ...state,
        filters: {
          einzelplan: null,
          kapitel: null,
          query: null,
          expenditureType: null,
          subExpType: null,
          funktion: null,
          oberfunktion: null,
          hauptfunktion: null,
          gruppierung: null,
          obergruppe: null,
          hauptgruppe: null,
          prefix: null,
          kapitelScope: null,
          type: state.filters.type, // Preserve the type when clearing filters
        },
        drillDownType: null,
        drillDownSubType: null,
        selectedTitle: null,
        brokenOutTitleIds: [],
      };

    case 'SET_YEAR':
      return {
        ...state,
        selectedYear: action.year,
      };

    case 'SET_TYPE':
      return {
        ...state,
        filters: {
          ...state.filters,
          type: action.budgetType,
        },
        // Reset drill-down and selected title when switching type
        drillDownType: null,
        drillDownSubType: null,
        selectedTitle: null,
        brokenOutTitleIds: [],
      };

    case 'DRILL_DOWN':
      return {
        ...state,
        drillDownType: action.expType,
        drillDownSubType: null,
        selectedTitle: null,
        brokenOutTitleIds: [],
        filters: {
          ...state.filters,
          expenditureType: null,
          // Keep a cross-parent spotlight sub-type (e.g. "Bildung") so the
          // sub-breakdown stays scoped to it — the single-sub-type auto-skip
          // then carries the drill straight to the parent's matching titles.
          subExpType: crossParentSubExpType(state.filters),
        },
      };

    case 'DRILL_DOWN_SUB':
      return {
        ...state,
        drillDownSubType: action.subType,
        selectedTitle: null,
        brokenOutTitleIds: [],
      };

    case 'DRILL_UP':
      // Go up one level at a time: selected title → level 3 → level 2 → level 1
      if (state.selectedTitle) {
        // From selected title back to level 3 (all titles for sub-type)
        return {
          ...state,
          selectedTitle: null,
          filters: {
            ...state.filters,
            expenditureType: null,
            // Preserve a cross-parent spotlight sub-type across drill-up too.
            subExpType: crossParentSubExpType(state.filters),
          },
        };
      }
      // Compare against null specifically: '' is a valid level-3 state for
      // auto-skipped categories (single sub-type) and Einnahmen. Treating ''
      // as "not at level 3" would skip this branch and clear drillDownType
      // while leaving drillDownSubType set, producing an inconsistent state
      // that renders "no data available".
      if (state.drillDownSubType !== null) {
        // From level 3 (titles) to level 2 (sub-types) — leaving the title set.
        return {
          ...state,
          drillDownSubType: null,
          brokenOutTitleIds: [],
          filters: {
            ...state.filters,
            subExpType: crossParentSubExpType(state.filters),
          },
        };
      }
      // From level 2 (sub-types) to level 1 (main types)
      return {
        ...state,
        drillDownType: null,
        drillDownSubType: null,
        brokenOutTitleIds: [],
        filters: {
          ...state.filters,
          expenditureType: null,
          subExpType: crossParentSubExpType(state.filters),
        },
      };

    case 'SET_DATA':
      return {
        ...state,
        data: action.data,
        isLoading: false,
        error: null,
      };

    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.isLoading,
      };

    case 'SET_ERROR':
      return {
        ...state,
        error: action.error,
        isLoading: false,
      };

    case 'SET_FILTER_OPTIONS':
      return {
        ...state,
        filterOptions: action.options,
      };

    case 'SELECT_TITLE':
      return {
        ...state,
        selectedTitle: action.title,
      };

    case 'SET_DISPLAY_UNIT':
      return {
        ...state,
        displayUnit: action.displayUnit,
      };

    case 'TOGGLE_TITLE_BREAKOUT': {
      const isBrokenOut = state.brokenOutTitleIds.includes(action.titleId);
      return {
        ...state,
        brokenOutTitleIds: isBrokenOut
          ? state.brokenOutTitleIds.filter((id) => id !== action.titleId)
          : [...state.brokenOutTitleIds, action.titleId],
      };
    }

    case 'CLEAR_TITLE_BREAKOUT':
      return {
        ...state,
        brokenOutTitleIds: [],
      };

    default:
      return state;
  }
}

export interface WidgetStateResult {
  state: WidgetState;
  setFilter: (key: keyof FilterState, value: string | null) => void;
  setFilterWithDrillDown: (key: keyof FilterState, value: string | null, drillDownType: string | null, drillDownSubType: string | null) => void;
  setFiltersWithDrillDown: (filters: Partial<FilterState>, drillDownType: string | null, drillDownSubType: string | null) => void;
  clearFilters: () => void;
  setYear: (year: number) => void;
  setType: (budgetType: 'E' | 'A') => void;
  drillDown: (expType: string) => void;
  drillDownSub: (subType: string) => void;
  drillUp: () => void;
  setData: (data: BudgetData) => void;
  setLoading: (isLoading: boolean) => void;
  setError: (error: string) => void;
  setFilterOptions: (options: FilterOptions) => void;
  selectTitle: (title: SelectedTitle | null) => void;
  setDisplayUnit: (displayUnit: DisplayUnit) => void;
  toggleTitleBreakout: (titleId: string) => void;
  clearTitleBreakout: () => void;
}

export function useWidgetState(
  initialFilters?: Partial<FilterState>,
  initialYear?: number,
  /** Box the state is mirrored into; a later mount starts from its content. */
  carry?: { current: WidgetState | null },
): WidgetStateResult {
  const [state, dispatch] = useReducer(widgetReducer, undefined, () => carry?.current ?? {
    ...initialState,
    filters: {
      ...initialState.filters,
      ...initialFilters,
    },
    selectedYear: initialYear || initialState.selectedYear,
  });
  useEffect(() => {
    if (carry) carry.current = state;
  }, [carry, state]);

  const setFilter = useCallback((key: keyof FilterState, value: string | null) => {
    dispatch({ type: 'SET_FILTER', key, value });
  }, []);

  const setFilterWithDrillDown = useCallback((
    key: keyof FilterState,
    value: string | null,
    drillDownType: string | null,
    drillDownSubType: string | null
  ) => {
    dispatch({ type: 'SET_FILTER_WITH_DRILL_DOWN', key, value, drillDownType, drillDownSubType });
  }, []);

  const setFiltersWithDrillDown = useCallback((
    filters: Partial<FilterState>,
    drillDownType: string | null,
    drillDownSubType: string | null
  ) => {
    dispatch({ type: 'SET_FILTERS_WITH_DRILL_DOWN', filters, drillDownType, drillDownSubType });
  }, []);

  const clearFilters = useCallback(() => {
    dispatch({ type: 'CLEAR_FILTERS' });
  }, []);

  const setYear = useCallback((year: number) => {
    dispatch({ type: 'SET_YEAR', year });
  }, []);

  const setType = useCallback((budgetType: 'E' | 'A') => {
    dispatch({ type: 'SET_TYPE', budgetType });
  }, []);

  const drillDown = useCallback((expType: string) => {
    dispatch({ type: 'DRILL_DOWN', expType });
  }, []);

  const drillDownSub = useCallback((subType: string) => {
    dispatch({ type: 'DRILL_DOWN_SUB', subType });
  }, []);

  const drillUp = useCallback(() => {
    dispatch({ type: 'DRILL_UP' });
  }, []);

  const setData = useCallback((data: BudgetData) => {
    dispatch({ type: 'SET_DATA', data });
  }, []);

  const setLoading = useCallback((isLoading: boolean) => {
    dispatch({ type: 'SET_LOADING', isLoading });
  }, []);

  const setError = useCallback((error: string) => {
    dispatch({ type: 'SET_ERROR', error });
  }, []);

  const setFilterOptions = useCallback((options: FilterOptions) => {
    dispatch({ type: 'SET_FILTER_OPTIONS', options });
  }, []);

  const selectTitle = useCallback((title: SelectedTitle | null) => {
    dispatch({ type: 'SELECT_TITLE', title });
  }, []);

  const setDisplayUnit = useCallback((displayUnit: DisplayUnit) => {
    dispatch({ type: 'SET_DISPLAY_UNIT', displayUnit });
  }, []);

  const toggleTitleBreakout = useCallback((titleId: string) => {
    dispatch({ type: 'TOGGLE_TITLE_BREAKOUT', titleId });
  }, []);

  const clearTitleBreakout = useCallback(() => {
    dispatch({ type: 'CLEAR_TITLE_BREAKOUT' });
  }, []);

  return {
    state,
    setFilter,
    setFilterWithDrillDown,
    setFiltersWithDrillDown,
    clearFilters,
    setYear,
    setType,
    drillDown,
    drillDownSub,
    drillUp,
    setData,
    setLoading,
    setError,
    setFilterOptions,
    selectTitle,
    setDisplayUnit,
    toggleTitleBreakout,
    clearTitleBreakout,
  };
}
